import { api, writeDB } from './store';
import { getOrder, Order } from './order';
import { BankAccount } from './company';
import rawPaymentMethods from '../../../config/payment.yml';
import { indexBy } from '../util/util';
import { t } from '../locales/translate';
import { camelize } from 'humps';
import { handleError, handleSuccess } from './notification';

/* INTERFACES */
export type PaymentStatus = 'pending' | 'paid' | 'failed';
export type PaymentMethodId =
  | 'paypal'
  | 'paypal_express'
  | 'stripe_card'
  | 'stripe_sofort'
  | 'stripe_checkout'
  | 'stripe_express'
  | 'cod'
  | 'bank_transfer'
  | 'amazon'
  | 'shopify';

export interface PaymentMethod {
  id: PaymentMethodId;
  immediate: boolean;
  name: string;
}

export interface Payment {
  id?: string;
  status: PaymentStatus;
  reference?: string;
  amount?: number;
  createdAt?: string;
  updatedAt?: string;
  paidAt?: string;
  paymentMethod: PaymentMethodId;
  externalPaymentId: string | null;
  documentUrl?: string;
  $permissions: string[];
  amountRemaining: number;
  bankAccount?: BankAccount;
  authorizationProcessable: boolean;
}

export interface PaymentModel {
  provider?: PaymentMethodId;
  amount?: number;
  skipEmailDelivery?: boolean;
  status?: PaymentStatus;
  bankAccount?: BankAccount;
}

export const paymentMethodsById = indexBy(
  rawPaymentMethods['methods'].map((e: PaymentMethod) => ({
    ...e,
    id: e.id.substr(1),
    name: t.model.bank[camelize(e.id.substr(1))] || e.id.substr(1).replace(/_/g, ' '),
  })),
  'id'
) as Record<PaymentMethodId, PaymentMethod>;
export const paymentMethods = Object.values(paymentMethodsById);

/* PAYMENTS CREATE */
export async function addPayment(
  orderId: string,
  { provider, amount, skipEmailDelivery, status, bankAccount }: PaymentModel
): Promise<void> {
  await api.createPayment({
    orderId,
    amount,
    provider,
    skipEmailDelivery,
    status,
    bankAccount,
  });
  const response = await api.showOrder({ orderId });

  // TODO: Update api model to match local interface
  const data = response.data as unknown as Order;
  writeDB.table('orders').update(orderId, data);
}

/* PAYMENTS READ */
/* PAYMENTS UPDATE */
export async function bookPayment(orderId: string, paymentId: string): Promise<void> {
  await api.updatePayment({ paymentId, status: 'paid' });
  await getOrder(orderId);
}

/* PAYMENTS DELETE */

export async function deletePayment(orderId: string, paymentId: string): Promise<void> {
  await api.destroyPayment({ paymentId });
  await getOrder(orderId);
}

export async function updateBankPayment(
  orderId: string,
  paymentId: string,
  { amount, bankAccount }: PaymentModel
): Promise<void> {
  try {
    await api.updatePayment({ paymentId, amount, bankAccount });
    handleSuccess(t.model.order.editSuccess);
  } catch {
    handleError(t.model.order.editFailed);
  }
  await getOrder(orderId);
}
